/// <amd-module name="TFS/CreateProject/Sources/OperationResultSource" />
import { Constants } from "TFS/CreateProject/Constants";
import { IOperationsRestClient } from "TFS/CreateProject/Generated/RestClient/Operations";
import * as CreateProjectResources from "TFS/CreateProject/Resources";
import { wait } from "VSS/Core/Util/Promise";
import { IVssPageContext } from "VSS/Platform/Context";
import { Operation, OperationStatus } from "VSS/Platform/Contracts/Operations";
import { issueRequest } from "VSS/Platform/Fetch";
import * as VSS_Trace from "VSS/Platform/Trace";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

export interface IOperationResult {
    isOperationResultSuccess: boolean;
    resultMessage?: string;
    resultUrl?: string;
}

export class OperationResultSource {
    private get operationsClient(): IOperationsRestClient {
        if (!this._operationsRestClient) {
            this._operationsRestClient = this._pageContext.getRestClient<IOperationsRestClient>("Operations_CreateProject.IOperationsRestClient");
        }

        return this._operationsRestClient;
    }
    private _operationsRestClient: IOperationsRestClient;
    public operationStatusPollTimeout: number = 2000;

    constructor(private _pageContext: IVssPageContext) {}

    /**
     * Polls for operation result and executes SuccessCallback and FailureCallback on Operation Success and Failure respectively.
     * Fetches the operation status and monitors further if required, and resolve/rejects the IOperationResult once we get it.
     * @param operationId - The id of the operation to be polled
     */
    public pollOperationResult(operationId: string): Promise<IOperationResult> {
        return this.operationsClient.getOperation(operationId).then(
            (operation: Operation) => {
                switch (operation.status) {
                    case OperationStatus.NotSet:
                    case OperationStatus.Queued:
                    case OperationStatus.InProgress:
                        return wait(this.operationStatusPollTimeout).then(() => this.pollOperationResult(operationId));
                    case OperationStatus.Succeeded:
                        return {
                            isOperationResultSuccess: true
                        };
                    case OperationStatus.Failed:
                    case OperationStatus.Cancelled:
                        return {
                            isOperationResultSuccess: false,
                            resultMessage: operation.resultMessage,
                            resultUrl: operation.resultUrl ? operation.resultUrl.resultUrl : undefined
                        };
                    default:
                        throw new Error(CreateProjectResources.UnknownOperationStateErrorMessage);
                }
            },
            (error: Error) => {
                VSS_Trace.traceError(
                    this._pageContext,
                    Constants.Area,
                    "CreateProjectPanel.OperationResultSource.PollOperationResult.Failed",
                    error.message,
                    { error: error }
                );

                // Polling the operation status failed
                throw error;
            }
        );
    }
}
